import { FC, useContext, useEffect, useMemo } from 'react'
import { useActiveFilterDispatch } from '@/providers/filterProvider/filtersProvider'
import CampaignProductsContainer from '@/components/organisms/campaignProductsContainer'
import PageContentWrapper from '@/components/organisms/pageContentWrapper'
import { Box, Flex } from '@/components/atoms/Grid'
import {
  ProductListingFetcher,
  ProductListingFetcherResponse
} from '@/components/organisms/productListing/productListingFetcher'
import { Config } from '@/core/config'
import { convertAPIProductToProduct } from 'types/bridgeProduct'
import {
  AdditionalInfoPageType,
  BannerHelper,
  Campaign,
  Category,
  HomeBanner,
  HomeBannerSize,
  ListingBanner,
  ListingProducts,
  Sdk
} from '@/network/graphql.g'
import {
  withFilterProvider,
  WithFilterProviderProps
} from '@/HOC/withFilterProvider'
import useGraphQl from 'hooks/useGraphQl'
import { requestThrow401 } from '@/utils/graphQl'
import { withBaseServerSideProps } from '@/HOC/withBaseServerSideProps'
import { useUser } from '@/providers/userProvider'
import { useMhub } from '@/providers/mhubProvider'
import { getCachableHeader } from '@/core/utils'
import useProductListing from 'hooks/useProductListing'
import { getProductListingData } from '@/components/organisms/productListing/productListingRedesign'
import { getMainData } from '@/utils/mainDataHelper'
import { SentryError } from '@/core/sentryError'
import { CacheType } from '@/utils/cache/pageDataCache'
import { GetServerSidePropsContext } from 'next'
import { useSpeedCurve } from '@/core/speedCurve/useSpeedCurve'
import { useRouter } from 'next/router'
import Cache from '@/utils/cache/redisServer'
import { MediaContextProvider } from '@/components/atoms/media'
import { CategoryTreeCampaignData } from '@/components/organisms/contentCategoryMenu/categoryMenuCampaignData'
import { Filter as FilterType } from '@/network/graphql.g'
import useDevice from 'hooks/useDevice'
import { useAdditionalInfoProvider } from '@/providers/additionalInfoProvider'
import { makeFilterInputFromQuery } from '@/providers/filterProvider/parsingFilters'
import { useNavigationSource } from '@/providers/navigationSourceProvider'
import { StoreContext, useDomain } from '@/providers/storeProvider'
import { useAsset } from '@/providers/assetsProvider'

type CampaignDetailProps = {
  campaignProducts: ListingProducts
  campaign: Campaign
  listingBanners: ListingBanner[]
  categoryProductBanners: HomeBanner[]
  filterId: string
  requestId: string
  isPreload: boolean
  categoryTree?: Category
} & WithFilterProviderProps

const createBanners = (...banners: BannerHelper[]): HomeBanner[] => {
  const result: HomeBanner[] = []
  banners.forEach((banner) => {
    banner?.desktopImage &&
      result.push({
        id: '',
        imageBig: banner.desktopImage,
        imageSmall: banner.mobileImage,
        url1: banner.url
      })
  })
  return result.length === 0 ? null : result
}

const CampaignDetail: FC<CampaignDetailProps> = ({
  campaignProducts,
  campaign,
  listingBanners,
  categoryProductBanners,
  filterId,
  requestId,
  isPreload,
  categoryTree
}) => {
  const { isLoggedIn, isEmployee } = useUser()
  const { subdomainName } = useDomain()
  const { isBezvasport } = useContext(StoreContext)
  const { setFilters } = useActiveFilterDispatch()
  const { requestNotifyError } = useGraphQl()
  const { isRsAndBaMarket } = useAsset()
  const router = useRouter()

  const isCampaignForCountDown = campaign?.id === '840' && !isRsAndBaMarket

  const { isDesktop } = useDevice()
  const { setParameters, setProductIds } = useAdditionalInfoProvider()

  useEffect(() => {
    const filter = makeFilterInputFromQuery(router?.query)

    setParameters(AdditionalInfoPageType.Category, campaign?.id, filter)
    return () => {
      setParameters(null, null, null)
    }
  }, [campaign?.id, router?.asPath])

  const fetcher: ProductListingFetcher = async (
    page,
    filterInput,
    refresh,
    initialLoad = false
  ): Promise<ProductListingFetcherResponse> => {
    const useNextProductList = page > 1

    const result = await requestNotifyError(
      (sdk: Sdk) => ({
        method: !useNextProductList
          ? sdk.getFirstProductsForCampaign
          : sdk.getNextProductsForCampaign,
        variables: {
          limit: Config.perPage,
          id: campaign.id,
          page: page,
          filter: filterInput,
          withFilterAndProductsCount: refresh
        },
        requestHeaders: getCachableHeader(filterInput, isLoggedIn)
      }),
      true
    )

    if (isEmployee) {
      setProductIds((prev) => [...prev, ...products.map((p) => p?.id)])
    }

    if (!result.success) {
      throw (
        result.error ??
        new SentryError('Campaign product listing', result.response)
      )
    }

    if (!useNextProductList && result.response?.campaignProducts) {
      if ('filter' in result.response.campaignProducts) {
        const filter = (result.response.campaignProducts as any).filter

        if (filter) {
          const moveFilterItems = (filtersArray: FilterType[]) => {
            const categoryIndex = filtersArray.findIndex(
              (filter) => filter.filterType === 'category'
            )
            const category2Index = filtersArray.findIndex(
              (filter) => filter.filterType === 'category2'
            )

            if (categoryIndex !== -1 && category2Index !== -1) {
              filtersArray[categoryIndex].filterItems = [
                ...filtersArray[category2Index].filterItems
              ]

              filtersArray[category2Index].filterItems = []
            }

            return filtersArray
          }

          const filterSortCategory = (filtersArray: FilterType[]) => {
            return filtersArray.map((f) => {
              if (f.filterType === 'category') {
                return {
                  ...f,
                  filterItems: [...f.filterItems].sort(
                    (a, b) => b.items.length - a.items.length
                  )
                }
              }
              return f
            })
          }

          const updatedFilters = isDesktop
            ? filterSortCategory(moveFilterItems(filter))
            : filterSortCategory(filter)

          setFilters(updatedFilters)
        }
      }
    }

    const { products, totalPagesCount } =
      result.response?.campaignProducts ?? {}

    !initialLoad && pushSimpleCampaignDetailPage(campaign, page)

    const productList =
      products?.map((product) => convertAPIProductToProduct(product)) ?? []

    if (!initialLoad && !router.query.a) {
      window.scrollTo(0, 0)
    }

    return {
      products: productList,
      totalPagesCount
    }
  }

  const { pushCampaignDetailPage, pushSimpleCampaignDetailPage } = useMhub()
  useEffect(() => {
    pushCampaignDetailPage(campaign)
  }, [campaign])

  useEffect(() => {
    if (isEmployee && campaignProducts?.products?.length) {
      setProductIds((prev) => [
        ...prev,
        ...campaignProducts.products.map((p) => p.id)
      ])
    }
  }, [campaign.id, isEmployee, router?.asPath])

  const { data } = useProductListing({
    totalPagesCount: campaignProducts.totalPagesCount,
    totalProductsCount: campaignProducts.totalProductsCount,
    id: campaign.id,
    products: campaignProducts?.products,
    banners: useMemo(
      () =>
        createBanners(campaign.bannerLeft, campaign.bannerRight)?.map(
          (banner, index, array) => ({
            ...banner,
            size: array.length === 1 ? HomeBannerSize.Big : HomeBannerSize.Small
          })
        ),
      [campaign.bannerLeft, campaign.bannerRight]
    ),
    listingBanners,
    filterId,
    requestId,
    isPreload,
    fetcher
  })

  useSpeedCurve({ label: 'Campaign' }, [router?.asPath])

  const isInSaleCampaign = campaign.id === '247'
  const isParfumCampaign = campaign.id === '852'

  const useFitIn = isParfumCampaign

  const { setNavigationSource } = useNavigationSource()

  useEffect(() => {
    setNavigationSource({
      from: 'campaign',
      name: campaign.title,
      slug: campaign.id
    })
  }, [campaign.title])

  const desktopImageUrl = isBezvasport
    ? `/InfoPromoPopUp/megaOutlet/Bezvasport/Desktop/${subdomainName}.webp`
    : `/InfoPromoPopUp/megaOutlet/Factcool/Desktop/${subdomainName}.webp`
  const mobileImageUrl = isBezvasport
    ? `/InfoPromoPopUp/megaOutlet/Bezvasport/Mobile/${subdomainName}.webp`
    : `/InfoPromoPopUp/megaOutlet/Factcool/Mobile/${subdomainName}.webp`

  return (
    <PageContentWrapper>
      <Box key={campaign.id}>
        <Flex>
          <MediaContextProvider>
            <CategoryTreeCampaignData
              categoryTree={categoryTree}
              router={router}
              campaignId={campaign.id}
              campaignProducts={campaignProducts}
            />
          </MediaContextProvider>
          <CampaignProductsContainer
            productListingFetcher={fetcher}
            data={data}
            description={campaign.description}
            showFilteredBy={true}
            list={`list.campaign.${campaign.name}`}
            useFitIn={useFitIn}
            showPager={true}
            isInSaleCampaign={isInSaleCampaign}
            isParfumCampaign={isParfumCampaign}
            campaignTitle={campaign.title}
            campaignId={campaign.id}
            isCampaignForCountDown={isCampaignForCountDown}
            categoryProductBanners={categoryProductBanners}
            desktopImageUrl={desktopImageUrl}
            mobileImageUrl={mobileImageUrl}
          />
        </Flex>
      </Box>
    </PageContentWrapper>
  )
}

export default withFilterProvider(CampaignDetail)

const getCommonProps = (
  context: GetServerSidePropsContext,
  props?: { requestId: string }
): { requestId: string } => ({
  requestId: props?.requestId ?? null
})

export const getServerSideProps = withBaseServerSideProps(
  async ({
    context,
    sdk,
    withMainNavigation,
    withStore,
    session
  }): Promise<GetServerSideProps<CampaignDetailProps>> => {
    const productListingData = getProductListingData(context)

    if ('redirect' in productListingData) {
      return {
        redirect: productListingData.redirect
      }
    }

    const { page, limit, filterInput, filterId, requestId, isPreload } =
      productListingData

    const campaignId = context.params.slug as string

    const isCategoryTreeInCache = await Cache.hasCategoriesTree(context)
    let categoriesTree: Category

    const { success, response } = await requestThrow401({
      method: sdk.getProductsForCampaign,
      variables: {
        limit,
        id: campaignId,
        filter: filterInput,
        page,
        withMainNavigation,
        withCategoriesTree: !isCategoryTreeInCache,
        withStore
      },
      requestHeaders: getCachableHeader(filterInput, session?.isLoggedIn),
      successPredicate: (response) =>
        !!(
          response?.campaignProducts &&
          response?.campaign &&
          response?.listingBannersForCampaign &&
          (isCategoryTreeInCache || response?.categoriesTreeJson)
        )
    })
    if (!success) {
      return {
        notFound: true
      }
    }

    if (isCategoryTreeInCache) {
      categoriesTree = await Cache.getCategoriesTree(context)
    } else {
      categoriesTree = JSON.parse(response?.categoriesTreeJson) as Category
      await Cache.setCategoriesTree(categoriesTree, context)
    }

    if (response?.seoTags) {
      response.seoTags.image =
        response?.campaignProducts.products?.find((item) => item)?.mainImage
          ?.url ?? null
    }

    return {
      props: {
        ...{
          mainData: getMainData(response),
          seoTags: response?.seoTags,
          filter: response?.campaignProducts.filter,
          campaignProducts: response?.campaignProducts,
          campaign: response?.campaign,
          listingBanners: response?.listingBannersForCampaign,
          categoryProductBanners:
            response?.homeBannersForHomeBannerCategory ?? null,
          filterId,
          isPreload,
          page,
          disableDaktela: true,
          categoryTree: categoriesTree
        },
        ...getCommonProps(context, { requestId })
      }
    }
  },
  { cacheType: CacheType.CampaignDetail, getCommonProps }
)
